import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  LogOut, LayoutDashboard, ListChecks, Link2, Cookie, MapPin,
  Droplets, ClipboardCheck, FileCheck2, AlertTriangle, DollarSign, Building2,
  ShieldCheck, FileText, FlaskConical, UserRound, Wallet, Briefcase, CreditCard, Inbox, Megaphone,
  Package, ShoppingCart, Users, LifeBuoy,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { OnboardingStatusProvider } from "@/lib/onboardingStatus";
import BusinessDetailsBanner from "@/components/partner/BusinessDetailsBanner";
import PartnerNotifBell from "@/components/partner/PartnerNotifBell";
import PartnerAttributionSummary from "@/components/partner/PartnerAttributionSummary";
import { useFeature } from "@/lib/useFeature";
import { Wrench } from "lucide-react";

// Feb 2026 — Partner portal nav split into TWO rows.
// Row 1 = daily workflow (things franchisees touch every day).
// Row 2 = setup & resources (things they configure occasionally).
// Kept a single flat map (`NAV_BY_KEY`) so page-level `active`
// props keep working with the same lowercase-label lookup they
// used before.
const NAV_ROW_ONE = [
  { to: "/partner/dashboard",           label: "Dashboard",       icon: LayoutDashboard, testid: "partner-nav-dashboard" },
  { to: "/partner/bookings",            label: "Bookings",        icon: ListChecks,      testid: "partner-nav-bookings"  },
  { to: "/partner/customers",           label: "Customers",       icon: Users,           testid: "partner-nav-customers" },
  { to: "/partner/leads",               label: "Quote Requests",  icon: Inbox,           testid: "partner-nav-leads" },
  { to: "/partner/reports",             label: "Reports",         icon: FileText,        testid: "partner-nav-reports"   },
  { to: "/partner/passports",           label: "Passports",       icon: ShieldCheck,     testid: "partner-nav-passports" },
  { to: "/partner/test-hub",            label: "AWARE Tools & Links", icon: FlaskConical, testid: "partner-nav-test-hub" },
  { to: "/partner/ledger",              label: "Ledger & Payouts",icon: Wallet,          testid: "partner-nav-ledger" },
];

const NAV_ROW_TWO = [
  { to: "/partner/marketing-resources", label: "Marketing & Referrals", icon: Megaphone,  testid: "partner-nav-marketing" },
  { to: "/partner/kit-orders",          label: "Order Sample Kits",   icon: Package,     testid: "partner-nav-kit-orders" },
  { to: "/partner/documents",           label: "Documents",           icon: FileText,    testid: "partner-nav-documents" },
  { to: "/partner/property-managers",   label: "Property Managers",   icon: Building2,   testid: "partner-nav-property-managers" },
  { to: "/partner/territory",           label: "My Territory",        icon: MapPin,      testid: "partner-nav-territory" },
  { to: "/partner/business-details",    label: "Business Details",    icon: Briefcase,   testid: "partner-nav-business-details" },
  { to: "/partner/payment-details",     label: "Payment Details",     icon: CreditCard,  testid: "partner-nav-payment-details" },
  { to: "/partner/profile",             label: "Profile",             icon: UserRound,   testid: "partner-nav-profile" },
  { to: "/partner/guide",               label: "Help & Guide",        icon: LifeBuoy,    testid: "partner-nav-guide" },
];

// Kept for pages that still import { NAV } for the `active` lookup
// or accessibility labels — flat list of every nav destination.
const NAV = [...NAV_ROW_ONE, ...NAV_ROW_TWO];

const METRIC_DEFS = [
  { key: "total_bookings",            label: "Total bookings",         icon: ListChecks,    tone: "bg-slate-100 text-slate-700",   testid: "partner-card-total" },
  { key: "new_bookings",              label: "New bookings",           icon: Droplets,      tone: "bg-cyan-100 text-cyan-800",     testid: "partner-card-new" },
  { key: "partner_link_bookings",     label: "Partner link",           icon: Link2,         tone: "bg-emerald-100 text-emerald-800", testid: "partner-card-partner-link" },
  { key: "stored_attribution_bookings", label: "Stored attribution",   icon: Cookie,        tone: "bg-amber-100 text-amber-800",    testid: "partner-card-stored" },
  { key: "region_allocated_bookings", label: "Region allocated",       icon: MapPin,        tone: "bg-indigo-100 text-indigo-800", testid: "partner-card-region" },
  { key: "samples_awaiting_return",   label: "Samples awaiting return", icon: Droplets,     tone: "bg-orange-100 text-orange-800", testid: "partner-card-samples" },
  { key: "results_pending",           label: "Results pending",        icon: ClipboardCheck, tone: "bg-blue-100 text-blue-800",     testid: "partner-card-results-pending" },
  { key: "results_published",         label: "Results published",      icon: FileCheck2,    tone: "bg-green-100 text-green-800",   testid: "partner-card-results-published" },
  { key: "flagged_or_failed",         label: "Flagged / failed",       icon: AlertTriangle, tone: "bg-rose-100 text-rose-800",     testid: "partner-card-flagged" },
];

export function PartnerShell({ children, active }) {
  const { user, logout } = useAuth();
  const { isVisible: correctiveLeadsOn } = useFeature("pm_corrective_lead_pipeline");
  const onLogout = async () => {
    // Await the cookie-clearing logout BEFORE the full-page redirect —
    // an aborted /auth/logout leaves the httpOnly cookie alive and the
    // session silently restores on refresh.
    await logout();
    window.location.assign("/login");
  };

  // Feature-gated Corrective Leads entry — inserted just after Quote
  // Requests so PWS Partners see it right next to their existing
  // inbound-lead flow.
  const rowOne = correctiveLeadsOn
    ? [
        ...NAV_ROW_ONE.slice(0, 3),
        { to: "/partner/corrective-leads", label: "Corrective Leads", icon: Wrench, testid: "partner-nav-corrective-leads" },
        ...NAV_ROW_ONE.slice(3),
      ]
    : NAV_ROW_ONE;

  return (
    <OnboardingStatusProvider>
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between gap-4">
            <Link to="/partner/dashboard" className="flex items-center gap-2 text-slate-900" data-testid="partner-header-home">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div className="leading-tight">
                <div className="text-sm font-bold text-slate-900">Aware Partner Portal</div>
                <div className="text-[11px] text-slate-500">Pristine Water Systems franchisee</div>
              </div>
            </Link>
            <div className="flex items-center gap-3">
              <PartnerNotifBell />
              <div className="hidden sm:flex flex-col items-end leading-tight">
                <span className="text-sm font-semibold text-slate-900" data-testid="partner-header-user-name">{user?.full_name}</span>
                <span className="text-xs text-slate-500" data-testid="partner-header-user-email">{user?.email}</span>
              </div>
              <button
                type="button"
                onClick={onLogout}
                data-testid="partner-logout-btn"
                className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700 hover:text-white bg-white hover:bg-slate-900 border border-slate-300 hover:border-slate-900 rounded-full px-3.5 py-2 transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" /> Sign out
              </button>
            </div>
          </div>
          <nav className="border-t border-slate-200 bg-white" data-testid="partner-nav">
            {[rowOne, NAV_ROW_TWO].map((row, rowIndex) => (
              <div
                key={rowIndex}
                data-testid={`partner-nav-row-${rowIndex + 1}`}
                className={`overflow-x-auto ${rowIndex === 1 ? "border-t border-slate-100 bg-slate-50/60" : ""}`}
              >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-1">
                  {row.map((n) => {
                    const Icon = n.icon;
                    const isActive = active === n.label.toLowerCase();
                    return (
                      <Link
                        key={n.to}
                        to={n.to}
                        data-testid={n.testid}
                        className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                          isActive
                            ? "border-cyan-600 text-cyan-700"
                            : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                        }`}
                      >
                        <Icon className="w-4 h-4" /> {n.label}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </header>
        <BusinessDetailsBanner />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</main>
      </div>
    </OnboardingStatusProvider>
  );
}

export default function PartnerDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/partner/dashboard");
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Unable to load dashboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const metrics = data?.metrics || {};
  const partner = data?.partner || {};

  return (
    <PartnerShell active="dashboard">
      <div className="mb-6" data-testid="partner-dashboard">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700 mb-1">Partner Dashboard</div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" data-testid="partner-dashboard-heading">
          {partner.business_name || "Partner Dashboard"}
        </h1>
        <div className="mt-1 text-sm text-slate-600" data-testid="partner-dashboard-region">
          {partner.franchise_region ? <span>{partner.franchise_region}</span> : null}
          {partner.partner_slug ? (
            <>
              {partner.franchise_region ? " · " : null}
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 font-mono">{partner.partner_slug}</span>
            </>
          ) : null}
        </div>
      </div>

      {loading && (
        <div className="rounded-xl bg-white border border-slate-200 p-8 text-center text-slate-500" data-testid="partner-dashboard-loading">
          Loading metrics…
        </div>
      )}
      {error && !loading && (
        <div className="rounded-xl bg-rose-50 border border-rose-200 p-6 text-rose-800" data-testid="partner-dashboard-error">
          {error}
        </div>
      )}
      {!loading && !error && (
        <>
          {/* Feb 2026 — Inventory hero. Prominent at top with colour-
              coded available count, purchased / used totals, and a
              low-stock warning + Order Sample Kits CTA when under 3. */}
          <KitInventoryHero partnerId={partner.id} />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="partner-metric-cards">
            {METRIC_DEFS.map((m) => {
              const Icon = m.icon;
              const value = metrics[m.key] ?? 0;
              return (
                <div
                  key={m.key}
                  data-testid={m.testid}
                  className="rounded-xl bg-white border border-slate-200 p-5 flex items-start gap-4 hover:shadow-sm transition-shadow"
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${m.tone}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{m.label}</div>
                    <div className="text-3xl font-bold text-slate-900 mt-0.5" data-testid={`${m.testid}-value`}>{value}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 rounded-xl bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-200 p-5" data-testid="partner-card-lead-value">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-cyan-600 text-white flex items-center justify-center">
                <DollarSign className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="text-[11px] uppercase tracking-wider font-bold text-cyan-800">Lead value snapshot</div>
                <div className="text-3xl font-bold text-slate-900 mt-0.5" data-testid="partner-card-lead-value-amount">
                  ${(metrics.lead_value_total_aud ?? 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-slate-600 mt-1">Total booking revenue attributed to your franchisee (excludes review-required allocations).</div>
              </div>
            </div>
          </div>

          <PartnerAttributionSummary />

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/partner/bookings"
              data-testid="partner-dashboard-view-bookings-cta"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-slate-900 hover:bg-cyan-700 rounded-full px-5 py-2.5 transition-colors"
            >
              View bookings <ListChecks className="w-4 h-4" />
            </Link>
            <Link
              to="/partner/test-hub"
              data-testid="partner-dashboard-test-hub-cta"
              className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-700 hover:text-white bg-cyan-50 hover:bg-cyan-700 border border-cyan-200 hover:border-cyan-700 rounded-full px-5 py-2.5 transition-colors"
            >
              Open AWARE Tools &amp; Links <FlaskConical className="w-4 h-4" />
            </Link>
          </div>
        </>
      )}
    </PartnerShell>
  );
}


// Feb 2026 — Partner Dashboard Inventory Hero.
//   Prominent card at the top of the dashboard showing available
//   kit stock with a colour-coded indicator:
//     🟢 10+ kits   🟡 3-9 kits   🔴 0-2 kits
//   Displays purchased / used totals + last 5 ledger entries, plus a
//   low-stock warning + Order Sample Kits CTA when < 3 kits.
function KitInventoryHero({ partnerId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/partner/kit-inventory")
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [partnerId]);

  if (loading || !data) return null;
  const available = Number(data.available || 0);
  const purchased = Number(data.ordered || 0);
  const used      = Number(data.used || 0);
  const history   = Array.isArray(data.history) ? data.history.slice(0, 5) : [];

  const tone =
    available >= 10 ? { chip: "bg-emerald-100 text-emerald-800", dot: "bg-emerald-500", ring: "ring-emerald-200", label: "In stock" }
    : available >= 3 ? { chip: "bg-amber-100 text-amber-800", dot: "bg-amber-500", ring: "ring-amber-200", label: "Running low" }
    : { chip: "bg-rose-100 text-rose-800", dot: "bg-rose-500", ring: "ring-rose-200", label: "Order now" };
  const lowStock = available < 3;
  // Feb 2026 — recommend 2 boxes (12 kits) when stock drops below
  // the alert threshold. Gets partner comfortably above the danger
  // zone in a single reorder without pushing an unreasonably large
  // Stripe checkout. Partner can still edit boxes on the picker.
  const recommendedBoxes = 2;
  const reorderHref = `/partner/kit-orders?boxes=${recommendedBoxes}&reorder=1`;

  return (
    <div
      data-testid="partner-kit-inventory-hero"
      className={`mb-6 rounded-2xl bg-white border ring-1 ${tone.ring} p-5 flex flex-col md:flex-row gap-5`}
    >
      <div className="flex items-start gap-4 md:min-w-[260px]">
        <div className={`w-14 h-14 rounded-xl ${tone.chip} flex items-center justify-center flex-shrink-0`}>
          <Package className="w-7 h-7" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Available sample kits</div>
          <div className="flex items-baseline gap-2 mt-0.5">
            <div className="text-5xl font-bold text-slate-900 tabular-nums" data-testid="partner-kit-inventory-available">{available}</div>
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${tone.chip}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`}></span>
              {tone.label}
            </span>
          </div>
          <div className="mt-2 text-[11px] text-slate-500 tabular-nums">
            <span data-testid="partner-kit-inventory-purchased">{purchased}</span> purchased · <span data-testid="partner-kit-inventory-used">{used}</span> used
          </div>
        </div>
      </div>

      <div className="flex-1 min-w-0">
        {lowStock && (
          <div
            className="mb-3 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2.5 text-xs text-rose-900 flex flex-wrap items-center gap-2"
            data-testid="partner-kit-inventory-low-stock"
          >
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span className="flex-1 min-w-[180px]">
              <strong className="font-bold">Low stock — {available} kit{available === 1 ? "" : "s"} left.</strong>
              {" "}Reorder now to keep serving partner-supplied bookings without a break.
            </span>
            <Link
              to={reorderHref}
              data-testid="partner-kit-inventory-reorder-now-cta"
              className="inline-flex items-center gap-1 rounded-full bg-rose-700 hover:bg-rose-800 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 transition-colors whitespace-nowrap"
            >
              <ShoppingCart className="w-3.5 h-3.5" /> Reorder now
            </Link>
          </div>
        )}
        {!lowStock && available < 10 && (
          <div
            className="mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 flex flex-wrap items-center gap-2"
            data-testid="partner-kit-inventory-running-low"
          >
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span className="flex-1">
              Running low — <strong className="font-bold">{available} kits</strong> remaining.
              Top up before the shed is empty.
            </span>
            <Link
              to={reorderHref}
              data-testid="partner-kit-inventory-running-low-cta"
              className="inline-flex items-center gap-1 text-amber-900 hover:text-amber-950 text-[11px] font-bold uppercase tracking-wider underline underline-offset-2"
            >
              Reorder <ShoppingCart className="w-3 h-3" />
            </Link>
          </div>
        )}
        {history.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Recent activity</div>
            <div className="rounded-lg border border-slate-100 divide-y divide-slate-100" data-testid="partner-kit-inventory-history">
              {history.map((row) => (
                <div key={row.id} className="px-3 py-1.5 flex items-center gap-2 text-[11px]">
                  <span className={`font-bold tabular-nums ${row.delta > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    {row.delta > 0 ? "+" : ""}{row.delta}
                  </span>
                  <span className="text-slate-700 flex-1 truncate">{row.reason}</span>
                  <span className="text-slate-400 text-[10px] tabular-nums">{new Date(row.created_at).toLocaleDateString("en-AU")}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex md:flex-col gap-2 justify-end md:min-w-[180px]">
        <Link
          to={lowStock ? reorderHref : "/partner/kit-orders"}
          data-testid="partner-kit-inventory-order-cta"
          className={`inline-flex items-center justify-center gap-1.5 rounded-lg ${lowStock ? "bg-rose-700 hover:bg-rose-800" : "bg-cyan-700 hover:bg-cyan-800"} text-white text-sm font-semibold px-4 py-2.5 transition-colors whitespace-nowrap`}
        >
          <Package className="w-4 h-4" /> {lowStock ? "Reorder now" : "Order Sample Kits"}
        </Link>
      </div>
    </div>
  );
}

